import { PlanIdToNameMap, ChargeBeePlanId } from '@prerender/billing-shared';

import { useCharbeePlanId } from '../../../../hooks/usePrerenderUser';

export type CacheExpirationOption = {
  key: string;
  value: number;
  label: string;
  upsellPlanId?: ChargeBeePlanId;
  upsellTooltip?: string;
};

const PLAN_LEVELS: Partial<Record<ChargeBeePlanId, number>> = {
  [ChargeBeePlanId.Starter]: 1,
  [ChargeBeePlanId.Growth]: 2,
  [ChargeBeePlanId.Pro]: 3,
  [ChargeBeePlanId.EnterprisePlus]: 4,
  [ChargeBeePlanId.EnterprisePlusYearly]: 4,
  [ChargeBeePlanId.CustomEnterprise3Years]: 4,
};

const CACHE_EXPIRATION_OPTIONS: CacheExpirationOption[] = [
  {
    key: '6',
    value: 0.25,
    label: '6 hours',
    upsellPlanId: ChargeBeePlanId.Pro,
  },
  {
    key: '12',
    value: 0.5,
    label: '12 hours',
    upsellPlanId: ChargeBeePlanId.Growth,
  },
  {
    key: '24',
    value: 1,
    label: '24 hours',
  },
  ...buildDaysOptions([2, 7]),
  ...buildDaysOptions([8, 14], ChargeBeePlanId.Growth),
  ...buildDaysOptions([15, 30], ChargeBeePlanId.Pro),
];

function useCacheExpirationOptions(): CacheExpirationOption[] {
  const currentPlanId = useCharbeePlanId();

  return CACHE_EXPIRATION_OPTIONS.map((option) => {
    if (!option.upsellPlanId) {
      return option;
    }

    const currentPlanLevel = PLAN_LEVELS[currentPlanId] || 0;
    const requiredPlanLevel = PLAN_LEVELS[option.upsellPlanId] || 0;

    if (currentPlanLevel >= requiredPlanLevel) {
      const { upsellPlanId, ...unlockedOption } = option;
      return unlockedOption;
    }
    const planName = PlanIdToNameMap[option.upsellPlanId];
    const upsellTooltip = `Click to upgrade to ${planName}`;
    return { ...option, upsellTooltip };
  });
}

function buildDaysOptions(daysInterval: [number, number], upsellPlanId?: ChargeBeePlanId): CacheExpirationOption[] {
  return Array.from({ length: daysInterval[1] - daysInterval[0] + 1 }, (_, index) => {
    const days = index + daysInterval[0];
    return {
      key: String(days * 24),
      value: days,
      label: `${days} days`,
      ...(upsellPlanId && { upsellPlanId }),
    };
  });
}

export default useCacheExpirationOptions;
